import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { calcDaysFromSeriesData } from '@/components/analytics/PeriodFilters.jsx';
import { useListContext } from 'react-admin';
import { Palette } from '@/themes/themeOverrides/Palette';
import PropTypes from 'prop-types';

const StyledBarChart = styled(BarChart)(() => ({
    '& .recharts-surface': {
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            overflow: 'visible',
        },
        '& path.recharts-rectangle[fill="#ccc"]': {
            fill: Palette.gray.gray100,
        },
        '& g.recharts-cartesian-grid-horizontal': {
            '& line': {
                stroke: Palette.gray.gray100,
                strokeDasharray: 'none',
            },
        },
        '& g.recharts-cartesian-grid-vertical': {
            '& line': {
                stroke: Palette.gray.gray100,
            },
        },
        '& path[stroke="#ccc"]': {
            stroke: Palette.gray.gray200,
        },
    },
    '&.product-count-analytics-chart, &.chart-analytics-service-closed-count': {
        '& g.recharts-layer': {
            '& path': {
                fill: Palette.blue.blue500,
            },
        },
    },
    '&.chart-analytics-service-closed-count': {
        position: 'relative',
        left: '-20px',
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            left: '0px',
        },
    },
    '& .recharts-text': {
        fontSize: '11px',
        fontWeight: '400',
        fill: Palette.black,
    },
    '& .recharts-cartesian-axis-tick-line': {
        stroke: Palette.gray.gray200,
    },
    '& .recharts-cartesian-axis-line': {
        stroke: Palette.gray.gray200,
    },
}));

const StyledTooltip = styled(Box)(() => ({
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: Palette.white,
    padding: '16px',
    borderRadius: '16px',
    boxShadow: Palette.boxShadow.elevation16,
    '& .MuiTypography-caption': {
        fontSize: '11px',
    },
}));

const StyledTooltipContent = styled(Box)(() => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
}));

const StyledTooltipTypography = styled(Typography)(() => ({
    fontWeight: 'bold',
    fontSize: '14px',
    '& .growth': {
        marginLeft: '4px',
        fontSize: '12px',
    },
}));

const formatDayMonth = tick => {
    const tickDate = new Date(tick);
    if (!tickDate || Number.isNaN(tickDate.getTime())) return '';

    const day = tickDate.getDate();
    const month = (tickDate.getMonth() + 1).toString().padStart(2, '0');

    return `${day}.${month}`;
};

const getMonthLocale = () => {
    if (typeof globalThis !== 'undefined' && globalThis.window?.localStorage) {
        const storedLocale = globalThis.window.localStorage.getItem('locale');
        if (storedLocale) {
            const lang = storedLocale.toLowerCase();
            if (lang.startsWith('pl')) return 'pl-PL';
            if (lang.startsWith('en')) return 'en-US';
        }
    }

    return 'pl-PL';
};

const formatMonthName = tick => {
    const tickDate = new Date(tick);
    if (!tickDate || Number.isNaN(tickDate.getTime())) return '';

    const locale = getMonthLocale();
    const isFullHD = typeof globalThis !== 'undefined' && typeof globalThis.window?.innerWidth === 'number' && globalThis.window.innerWidth >= 1920;
    const monthName = tickDate.toLocaleDateString(locale, { month: isFullHD ? 'long' : 'short' });

    if (!monthName) return '';

    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
};

const getTickFormatter = days => {
    return tick => {
        if (days >= 180) {
            return formatMonthName(tick);
        }

        return formatDayMonth(tick);
    };
};

const CustomTick = props => {
    const { className = '', payload, tickFormatter, dy = '0.71em', ...restProps } = props;

    return (
        <text {...restProps} className={`recharts-text ${className}`}>
            <tspan dy={dy}>{tickFormatter(payload.value)}</tspan>
        </text>
    );
};

const getTick = (days, length, isCustomRange) => {
    let tick;
    if (days >= 14 && days < 30) {
        tick = props => {
            const { index } = props;
            if (index % 2 === 1) {
                return <CustomTick {...props} />;
            }
            return null;
        };
    } else if (days >= 30 && days < 90) {
        tick = props => {
            const { index } = props;
            if ((index - 1) % 4 === 0 && index >= 1) {
                return <CustomTick {...props} />;
            }
            return null;
        };
    } else if (days >= 90 && days < 180) {
        if (isCustomRange) {
            tick = props => {
                const { index } = props;
                if (index % 4 === 1) {
                    return <CustomTick {...props} />;
                }
                return null;
            };
        } else {
            let xs = [];
            tick = props => {
                const { x, index, payload, tickFormatter, dy = '0.71em', className = '' } = props;
                xs.push(x);
                if (index % 4 === 2 && index > 0) {
                    let xMiddle = (xs[index] + xs[index - 1]) / 2;
                    return (
                        <text {...props} x={xMiddle} className={`recharts-text ${className}`}>
                            <tspan dy={dy}>{tickFormatter(payload.value)}</tspan>
                        </text>
                    );
                } else if (index + 1 === length) {
                    let barCount = index % 4;
                    if (barCount > 1) return null;

                    let xMiddle = barCount === 0 ? x : (xs[index] + xs[index - 1]) / 2;

                    return (
                        <text {...props} x={xMiddle} className={`recharts-text ${className}`}>
                            <tspan dy={dy}>{tickFormatter(payload.value)}</tspan>
                        </text>
                    );
                }

                return null;
            };
        }
    } else if (days < 14 || days >= 180) {
        tick = props => {
            return <CustomTick {...props} />;
        };
    }

    return tick;
};

const getXAxisProps = (days, length, isCustomRange) => ({
    tickFormatter: getTickFormatter(days),
    tick: getTick(days, length, isCustomRange),
    interval: 0,
    dataKey: 'periodStart.date',
});

const getYAxisProps = (percent, isMdUp) => {
    let props = {};

    if (percent) {
        props.tickFormatter = tick => `${tick * 100}%`;
        props.domain = [0, 1];
    }

    props.width = isMdUp ? 60 : 25;

    return props;
};

export const getFillColor = (avg, value, theme, thresholds) => {
    if (value >= thresholds[0] * avg) {
        return Palette.green.green500;
    }

    if (value >= thresholds[1] * avg) {
        return Palette.yellow.yellow500;
    }

    return Palette.red.red500;
};

const getPath = (x, y, width, height) => {
    const radius = 4;
    return `M${x},${y + height}
            L${x},${y + radius}
            Q${x},${y} ${x + radius},${y}
            L${x + width - radius},${y}
            Q${x + width},${y} ${x + width},${y + radius}
            L${x + width},${y + height}
            Z`;
};

const RoundedTopBar = props => {
    const { fill, x, y, width, height } = props;

    if (height <= 0) {
        return null;
    }

    return <path d={getPath(x, y, width, height)} stroke='none' fill={fill} />;
};

const formatValue = (value, percent) => {
    if (percent) {
        return `${formatNumber(value * 100, 1)}%`;
    }

    return value;
};

const isIncompletePeriod = (entry, days, isLastBucket) => {
    if (days < 90 || !isLastBucket) {
        return false;
    }

    const startRaw = entry?.periodStart?.date;
    const endRaw = entry?.periodEnd?.date;
    if (!startRaw || !endRaw) {
        return false;
    }

    const start = new Date(startRaw);
    const end = new Date(endRaw);
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (days >= 30 && days < 180) {
        return start <= today && today <= end;
    }

    if (days >= 180) {
        return start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth();
    }

    return false;
};

const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') return hex;

    const normalizedHex = hex.replace('#', '');
    if (normalizedHex.length !== 6) return hex;

    const r = Number.parseInt(normalizedHex.slice(0, 2), 16);
    const g = Number.parseInt(normalizedHex.slice(2, 4), 16);
    const b = Number.parseInt(normalizedHex.slice(4, 6), 16);

    if ([r, g, b].some(v => Number.isNaN(v))) return hex;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatRangeDate = date => {
    const d = new Date(date);
    if (!d || Number.isNaN(d.getTime())) return '';

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');

    return `${day}.${month}`;
};

const CustomTooltip = ({ active, payload, secondaryDataKey, secondaryLabel, percent = false }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const dataKey = payload[0].dataKey;
    const label = payload[0].name;
    const previousDataKey = `${dataKey}Previous`;
    const data = payload[0].payload;
    const current = data[dataKey];
    const previous = data[previousDataKey];
    const growth = ((current - previous) / previous) * 100;
    const value = formatValue(data[dataKey], percent);
    const secondaryValue = secondaryDataKey ? formatValue(data[secondaryDataKey], percent) : null;

    let rangeLabel = null;
    if (data?.periodStart?.date && data?.periodEnd?.date) {
        const startDate = new Date(data.periodStart.date);
        const endDate = new Date(data.periodEnd.date);

        const isSameDay =
            !Number.isNaN(startDate.getTime()) &&
            !Number.isNaN(endDate.getTime()) &&
            startDate.getFullYear() === endDate.getFullYear() &&
            startDate.getMonth() === endDate.getMonth() &&
            startDate.getDate() === endDate.getDate();

        rangeLabel = isSameDay ? formatRangeDate(startDate) : `${formatRangeDate(data.periodStart.date)} - ${formatRangeDate(data.periodEnd.date)}`;
    }

    return (
        <StyledTooltip>
            {rangeLabel && <StyledTooltipTypography variant='caption'>{rangeLabel}</StyledTooltipTypography>}
            <Typography variant='caption'>{label}</Typography>
            <StyledTooltipContent>
                <StyledTooltipTypography>{value}</StyledTooltipTypography>
                <Growth value={growth} className='growth--small' />
            </StyledTooltipContent>
            {secondaryValue && (
                <>
                    <Typography variant='caption'>{secondaryLabel}</Typography>
                    <StyledTooltipContent>
                        <StyledTooltipTypography>{secondaryValue}</StyledTooltipTypography>
                    </StyledTooltipContent>
                </>
            )}
        </StyledTooltip>
    );
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            dataKey: PropTypes.string,
            name: PropTypes.string,
            payload: PropTypes.shape({
                periodStart: PropTypes.shape({
                    date: PropTypes.string,
                }),
                periodEnd: PropTypes.shape({
                    date: PropTypes.string,
                }),
            }),
        }),
    ),
    secondaryDataKey: PropTypes.string,
    secondaryLabel: PropTypes.string,
    percent: PropTypes.bool,
};

export const BarChartWithTrend = ({ data, dataKey, label, secondaryDataKey, secondaryLabel, percent = false, barColorThresholds = [0.75, 0.5], className }) => {
    const { filterValues } = useListContext();
    const theme = useTheme();
    const isMdUp = useMediaQuery(`(min-width:${breakpoints.values.md}px)`);
    const avg = data.map(d => d[dataKey]).reduce((a, b) => a + b, 0) / data.length;
    const isCustomRange = filterValues.period === 'custom';
    const days = isCustomRange ? calcDaysFromSeriesData(data) : parseInt(filterValues.period);

    return (
        <ResponsiveContainer width='100%'>
            <StyledBarChart data={data} maxBarSize={38} className={className}>
                <CartesianGrid strokeDasharray='3 3' stroke={Palette.gray.gray200} />
                <XAxis {...getXAxisProps(days, data.length, isCustomRange)} />
                <YAxis {...getYAxisProps(percent, isMdUp)} />
                <Tooltip
                    content={<CustomTooltip percent={percent} secondaryDataKey={secondaryDataKey} secondaryLabel={secondaryLabel} days={days} />}
                    position={{ x: 'auto', y: 20 }}
                />
                <Bar dataKey={dataKey} name={label} shape={<RoundedTopBar />}>
                    {data.map((entry, index) => {
                        const isLastBucket = index === data.length - 1;
                        const baseColor = getFillColor(avg, entry[dataKey], theme, barColorThresholds);
                        const fill = isIncompletePeriod(entry, days, isLastBucket) ? hexToRgba(baseColor, 0.5) : baseColor;
                        const key = entry?.periodStart?.date || entry?.periodEnd?.date || `${dataKey}-${index}`;

                        return <Cell key={key} fill={fill} />;
                    })}
                </Bar>
            </StyledBarChart>
        </ResponsiveContainer>
    );
};

BarChartWithTrend.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    dataKey: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    secondaryDataKey: PropTypes.string,
    secondaryLabel: PropTypes.string,
    percent: PropTypes.bool,
    barColorThresholds: PropTypes.arrayOf(PropTypes.number),
    className: PropTypes.string,
};
